import { isDefined } from "@clipboard-health/util-ts";
import { Box, ButtonBase, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { Avatar } from "@src/appV2/lib/Avatar";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { useDisplayTime } from "@src/appV2/redesign/components/LocalTime/useDisplayTime";
import { Text } from "@src/appV2/redesign/components/Text";
import { useModalState } from "@src/lib/ui-react";
import { useCallback } from "react";

import { type AccountSwitcherUser } from "../../Shared/AccountSwitcher/types";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { getCityFromTimezone } from "../../Shared/Header/timezone";
import { WorkplaceSelectorSheet } from "./WorkplaceSelectorSheet";

export function AccountSwitcherMobile() {
  const { admin, user } = useSession();

  // Admins get their switcher via the Omnisearch button in the footer —
  // there's no inline account switcher in the mobile header for them.
  if (admin) {
    return null;
  }

  // Workplace users only get the switcher if they actually have another
  // workplace to switch to — a single-workplace user has nothing to pick.
  const workplaceCount = user?.facilityIds?.length ?? 0;
  if (workplaceCount <= 1) {
    return null;
  }

  return <WorkplaceSwitcherMobile />;
}

/**
 * Workplace user: avatar button opens a bottom sheet listing the associated
 * workplaces the user can switch into.
 */
function WorkplaceSwitcherMobile() {
  const { switcherTitle, workplaceDetails, onUserClicked } = useAccountSwitcher();
  const { profile, tz } = useSession();
  const sheetModalState = useModalState();
  const localTime = useDisplayTime({ timeZone: tz });

  // Switcher acts as the drawer title, so the button shows the currently
  // selected workplace name.
  const buttonLabel = switcherTitle;
  const city = isDefined(tz) ? getCityFromTimezone(tz) : undefined;
  const showSubtitle = isDefined(localTime) && isDefined(city);

  const handleWorkplaceSelected = useCallback(
    (selected: AccountSwitcherUser) => {
      sheetModalState.closeModal();
      onUserClicked(selected);
    },
    [onUserClicked, sheetModalState]
  );

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <ButtonBase
        onClick={sheetModalState.openModal}
        aria-label={`Switch account (current: ${switcherTitle})`}
        aria-haspopup="dialog"
        aria-expanded={sheetModalState.modalIsOpen}
        sx={(theme) => ({
          width: "100%",
          minWidth: 0,
          minHeight: 52,
          paddingX: 3,
          paddingY: 2,
          gap: 3,
          borderRadius: theme.borderRadius?.medium,
          backgroundColor: theme.palette.navbar?.itemSelectedBackground,
          color: theme.palette.navbar?.itemContent,
        })}
      >
        <Avatar
          name={buttonLabel}
          size="small"
          sx={(theme) => ({
            bgcolor: theme.palette.navbar?.avatarBackground,
            color: theme.palette.navbar?.avatarText,
            flexShrink: 0,
          })}
        />
        <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
          <Text
            variant="body1"
            semibold
            textAlign="left"
            noWrap
            sx={(theme) => ({ minWidth: 0, color: theme.palette.navbar?.userName })}
          >
            {buttonLabel}
          </Text>
          {showSubtitle && (
            <Text
              variant="caption"
              textAlign="left"
              noWrap
              sx={(theme) => ({
                color: theme.palette.navbar?.itemContent,
                letterSpacing: "0.02em",
              })}
            >
              {localTime} · {city}
            </Text>
          )}
        </Stack>
        <Icon type="caret-bottom" sx={{ flexShrink: 0 }} />
      </ButtonBase>

      <WorkplaceSelectorSheet
        modalState={sheetModalState}
        workplaceDetails={workplaceDetails}
        selectedWorkplaceId={profile?.userId}
        onWorkplaceSelected={handleWorkplaceSelected}
      />
    </Box>
  );
}
